import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PsychologyIcon from '@mui/icons-material/Psychology';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import GavelIcon from '@mui/icons-material/Gavel';
import TuneIcon from '@mui/icons-material/Tune';
import Sidebar from '../Sidebar';
import AppHeader from '../AppHeader';
import { getOntologySummary, normalizeSummary } from '../../api/ontologyApi';
import { dsVar } from '../../theme/tokens';

/**
 * The way in to the ontology.
 *
 * This screen exists because the three tabs behind it are named after the DATA MODEL, and
 * nobody arrives asking "show me all parameters". They arrive with a question — why did
 * this alert fire, are we covered for this regulation, what are we running and who
 * approved it — so the cards are phrased as those questions and the search box, which
 * needs no knowledge of the model at all, comes first.
 *
 * Counts are decoration. Every card navigates before the summary request resolves and
 * still navigates if it fails, because a number nobody has yet is not a reason to
 * withhold a link.
 */

interface EntryCard {
    id: string;
    title: string;
    blurb: string;
    icon: React.ReactNode;
    href: string;
    /** Rendered under the blurb once counts arrive. */
    metric: (summary: ReturnType<typeof normalizeSummary>) => string;
}

const CARDS: EntryCard[] = [
    {
        /**
         * FIRST, because it is where the chain starts: a regulation or an internal policy
         * is what the terms, policies and thresholds below it were derived FROM.
         *
         * It is also the only card that answers a question the others cannot — "we are
         * revising this document, what does it touch?" — which until now had no entry
         * point at all, because a citation was a display string with no reverse index.
         */
        id: 'documents',
        title: 'What we must comply with',
        blurb: 'The guidance, statute and internal policy behind the library — and what a change to any of them would reach.',
        icon: <MenuBookIcon />,
        href: '/ontology/documents',
        metric: (summary) => `${summary.sourceDocuments.total} documents`,
    },
    {
        id: 'library',
        title: 'What we look for',
        blurb: 'Red flags, laundering typologies, the controls that detect them and the measures they are tested against.',
        icon: <PsychologyIcon />,
        href: '/ontology/concepts',
        metric: (summary) => `${summary.concepts.total} terms`,
    },
    {
        id: 'policies',
        title: 'Rules we follow',
        blurb: 'The policies and regulatory obligations that govern those terms, and what each one requires of you.',
        icon: <GavelIcon />,
        href: '/ontology/policies',
        metric: (summary) => `${summary.policies.total} policies`,
    },
    {
        id: 'thresholds',
        title: "How we're tuned",
        blurb: 'The thresholds your agents are running right now, where each value came from, and what is waiting on a decision.',
        icon: <TuneIcon />,
        // Lands on the worklist, which is the reason to come here rather than browse.
        href: '/ontology/parameters?status=draft&scope_level=organisation',
        metric: (summary) => (
            summary.parameters.orgDrafts === 0
                ? `${summary.parameters.total} thresholds · none awaiting approval`
                : `${summary.parameters.orgDrafts} awaiting approval`
        ),
    },
];

const OntologyHome: React.FC = () => {
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement>(null);

    const [query, setQuery] = useState('');
    const [summary, setSummary] = useState<ReturnType<typeof normalizeSummary> | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        let active = true;
        (async () => {
            try {
                const res = await getOntologySummary({ signal: controller.signal });
                if (active) setSummary(normalizeSummary(res));
            } catch {
                // Deliberately silent. The cards are the page; counts are a garnish, and an
                // error banner over three working links would be noise about nothing the
                // user asked for.
                if (active) setSummary(null);
            }
        })();
        return () => { active = false; controller.abort(); };
    }, []);

    useEffect(() => { inputRef.current?.focus(); }, []);

    const submitSearch = (event: React.FormEvent) => {
        event.preventDefault();
        const trimmed = query.trim();
        if (trimmed) navigate(`/ontology/search?q=${encodeURIComponent(trimmed)}`);
    };

    return (
        <div className="flex h-screen overflow-hidden bg-page font-sans text-fg">
            <Sidebar activePage="ontology" />

            <div className="flex flex-1 flex-col overflow-hidden">
                <AppHeader
                    left={(
                        <Typography
                            component="h1"
                            sx={{ fontSize: '1.125rem', fontWeight: 600, color: dsVar('fg') }}
                        >
                            Policy &amp; Risk Library
                        </Typography>
                    )}
                />

                <main className="flex-1 overflow-y-auto px-8 pb-16">
                    <Box sx={{ maxWidth: '64rem', mx: 'auto', pt: 6 }}>
                        <Typography
                            component="p"
                            sx={{ fontSize: '0.9375rem', color: dsVar('fg-2'), lineHeight: 1.7, mb: 3 }}
                        >
                            Everything your agents reason with: what they look for, the rules
                            behind it, and how it is tuned. Platform and regulatory entries are
                            shared and read-only — customise one to create your organisation&apos;s
                            own version.
                        </Typography>

                        {/* Search leads because it is the only route in that requires no
                            knowledge of how any of this is filed. */}
                        <Box component="form" onSubmit={submitSearch} sx={{ mb: 5 }}>
                            <TextField
                                inputRef={inputRef}
                                fullWidth
                                placeholder="Search for a term, a rule or a threshold — try “structuring”"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: 20, color: dsVar('fg-muted') }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: dsVar('surface'),
                                        color: dsVar('fg-body'),
                                        fontSize: '0.9375rem',
                                        '& fieldset': { borderColor: dsVar('line') },
                                        '&:hover fieldset': { borderColor: dsVar('line') },
                                        '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') },
                                    },
                                }}
                            />
                        </Box>

                        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                            {CARDS.map((card) => (
                                <button
                                    key={card.id}
                                    type="button"
                                    onClick={() => navigate(card.href)}
                                    className="flex flex-col items-start rounded-xl border border-line bg-page/40 p-6 text-left transition-colors hover:border-accent-fg/50"
                                >
                                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg border border-accent-fg/30 bg-accent/20 text-accent-fg">
                                        {card.icon}
                                    </div>

                                    <Typography
                                        component="h2"
                                        sx={{ fontSize: '1rem', fontWeight: 600, color: dsVar('fg'), mb: 0.75 }}
                                    >
                                        {card.title}
                                    </Typography>

                                    <Typography
                                        sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), lineHeight: 1.6, flexGrow: 1 }}
                                    >
                                        {card.blurb}
                                    </Typography>

                                    {/* A fixed-height slot, so the cards do not reflow when
                                        the counts land a moment after the page. */}
                                    <Box sx={{ mt: 2, minHeight: 18 }}>
                                        {summary && (
                                            <Typography
                                                sx={{
                                                    fontFamily: '"JetBrains Mono", monospace',
                                                    fontSize: '0.75rem', color: dsVar('accent-fg'),
                                                }}
                                            >
                                                {card.metric(summary)}
                                            </Typography>
                                        )}
                                    </Box>
                                </button>
                            ))}
                        </div>
                    </Box>
                </main>
            </div>
        </div>
    );
};

export default OntologyHome;
